import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getCurrentSession, onAuthStateChange, type CurrentSession } from "../lib/auth";

interface AuthGuardProps {
	children: ReactNode;
}

// Route guard. Renders a loading shell while the session is being hydrated
// (via GET /api/me/session — 200 → signed in, 401 → no cookie), redirects to
// /auth/login when there's no session, and renders children otherwise.
export function AuthGuard({ children }: AuthGuardProps) {
	const location = useLocation();
	const [loading, setLoading] = useState(true);
	const [session, setSession] = useState<CurrentSession | null>(null);

	useEffect(() => {
		let cancelled = false;
		void getCurrentSession().then((s) => {
			if (!cancelled) {
				setSession(s);
				setLoading(false);
			}
		});
		const unsub = onAuthStateChange((s) => {
			setSession(s);
			setLoading(false);
		});
		return () => {
			cancelled = true;
			unsub();
		};
	}, []);

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center text-muted">
				<span>Loading…</span>
			</div>
		);
	}

	if (!session) {
		const next = encodeURIComponent(location.pathname + location.search);
		return <Navigate to={`/auth/login?next=${next}`} replace />;
	}

	return <>{children}</>;
}
